// Identificação do cliente no Totem (e no Balcão quando o PIX online exige
// CPF). Três etapas: "Deseja se identificar?" → CPF → nome (só se o CPF não
// tiver cadastro). Achou o cadastro: segue com o nome de lá. Decisão do dono
// (09/09/2026): é assim que a loja cadastra mais gente no banco.
//
//   etapa: "pergunta" | "cpf" | "nome"
//   motivoPix: veio do pagamento PIX (sem opção "não", com explicação).
//   cashback: { percentual, ganho } quando o cashback está ligado pro módulo —
//     a pergunta vira "Ganhe Cashback"; `ganho` (valor deste pedido) aparece
//     quando o cliente veio do botão do pagamento (motivoCashback).
//   onIdentificado({ id, nome, cpf, foto }) · onPular() · onVoltar()

import { ArrowLeft, ChevronRight, Coins, UserRound } from "lucide-react";
import { useState } from "react";

import { cpfValido, formatarCpf } from "../pix-cobranca/lib/cpf";
import BotaoApagar from "./BotaoApagar";
import TecladoAlfabetico from "./TecladoAlfabetico";
import TecladoNumerico from "./TecladoNumerico";
import { totemApi } from "./api";

export default function TelaIdentificacao({ etapa, setEtapa, motivoPix = false, cashback = null, motivoCashback = false, onIdentificado, onPular, onVoltar }) {
  const [cpf, setCpf] = useState("");
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState("");
  const [buscando, setBuscando] = useState(false);
  const digitos = cpf.replace(/\D/g, "");

  const buscar = async () => {
    if (!cpfValido(digitos)) { setErro("CPF inválido. Confira os números."); return; }
    setBuscando(true); setErro("");
    try {
      const r = await totemApi.buscarPorCpf(digitos);
      onIdentificado({ id: r.data.id, nome: r.data.nome || "", cpf: digitos, foto: r.data.foto || null, novo: false });
    } catch (e) {
      if (e?.response?.status === 404) { setEtapa("nome"); return; }
      setErro(e?.response?.data?.detail || "Não foi possível consultar o CPF. Tente de novo.");
    } finally { setBuscando(false); }
  };

  // Teclado da tela manda maiúsculas; grava "Nome Sobrenome".
  const acrescentar = (l) => setNome((n) => (n.endsWith(" ") || n === "" ? n + l.toUpperCase() : n + l.toLowerCase()));

  const nomeValido = nome.trim().replace(/[^A-Za-zÀ-ÿ]/g, "").length >= 3;

  const cadastrar = async () => {
    const n = nome.trim();
    if (!nomeValido) { setErro("O nome precisa ter pelo menos 3 letras."); return; }
    setBuscando(true); setErro("");
    try {
      const r = await totemApi.criarCliente({ nome: n, cpf: digitos });
      onIdentificado({ id: r.data.id, nome: n, cpf: digitos, foto: null, novo: true });
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível cadastrar. Tente de novo.");
    } finally { setBuscando(false); }
  };

  const Cabecalho = ({ titulo, voltar }) => (
    <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-4 flex items-center gap-3">
      <button type="button" onClick={voltar} className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center" aria-label="Voltar"><ArrowLeft className="w-5 h-5 text-zinc-300" /></button>
      <h2 className="text-xl font-bold text-zinc-100">{titulo}</h2>
    </div>
  );

  if (etapa === "pergunta") {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col select-none" data-e2e="tela-identificacao">
        <Cabecalho titulo="Identificação" voltar={onVoltar} />
        <div className="flex-1 flex flex-col items-center justify-center px-8 gap-10">
          <div className="text-center">
            {cashback ? (
              <div className="w-24 h-24 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6"><Coins className="w-12 h-12 text-emerald-400" /></div>
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6"><UserRound className="w-12 h-12 text-primary" /></div>
            )}
            <h2 className="text-4xl md:text-5xl font-black text-zinc-100 mb-3" data-e2e="identificacao-titulo">{cashback ? "Ganhe Cashback" : "Deseja se identificar?"}</h2>
            <p className="text-lg text-zinc-400">
              {motivoPix
                ? "Para pagar com PIX online precisamos do seu CPF."
                : cashback
                  ? `Informe seu CPF e ganhe ${String(cashback.percentual).replace(".", ",")}% de volta em cada compra pra usar nas próximas. Sem conta, você vê o cashback mas não acumula.`
                  : "Com o CPF a gente te chama pelo nome e agiliza o pagamento."}
            </p>
          </div>
          <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button type="button" onClick={() => { setErro(""); setEtapa("cpf"); }} data-e2e="identificar-sim" className="h-24 rounded-2xl bg-primary text-primary-foreground text-2xl font-black shadow-lg active:scale-95 transition-transform">SIM, INFORMAR CPF</button>
            {!motivoPix && (
              <button type="button" onClick={onPular} data-e2e="identificar-nao" className="h-24 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-2xl font-bold active:scale-95 transition-transform">NÃO, CONTINUAR</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (etapa === "cpf") {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col select-none" data-e2e="tela-cpf">
        <Cabecalho titulo="Seu CPF" voltar={() => setEtapa("pergunta")} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 overflow-y-auto">
          <h2 className="text-3xl md:text-4xl font-black text-zinc-100 text-center">Digite seu CPF</h2>
          {motivoCashback && cashback?.ganho > 0 && (
            <p className="flex items-center gap-2 text-emerald-300 font-semibold -mt-3" data-e2e="cpf-motivo-cashback"><Coins className="w-5 h-5" /> Informe o CPF pra ganhar R$ {Number(cashback.ganho).toFixed(2).replace(".", ",")} de cashback neste pedido</p>
          )}
          {/* Uma linha só: sem quebra, espaçamento curto e fonte que cabe na tela em pé do totem */}
          <div className="relative w-full max-w-lg">
            <div className="h-20 pr-20 pl-5 rounded-2xl bg-zinc-900 border-2 border-zinc-700 flex items-center justify-center text-3xl md:text-4xl font-mono tracking-wide tabular-nums whitespace-nowrap overflow-hidden text-zinc-100" data-e2e="cpf-valor">
              {cpf || <span className="text-zinc-600">000.000.000-00</span>}
            </div>
            <BotaoApagar
              onApagar={() => { setErro(""); setCpf(formatarCpf(digitos.slice(0, -1))); }}
              onLimpar={() => { setErro(""); setCpf(""); }}
              desabilitado={!digitos}
            />
          </div>
          {erro && <p className="text-red-400 font-semibold" data-e2e="cpf-erro">{erro}</p>}
          <TecladoNumerico onDigito={(d) => { setErro(""); setCpf(formatarCpf(digitos + d)); }} />
        </div>
        <div className="bg-zinc-900 border-t border-zinc-800 px-6 py-3">
          <div className="max-w-lg mx-auto">
            <button type="button" onClick={buscar} disabled={digitos.length !== 11 || buscando} data-e2e="cpf-avancar" className="w-full h-14 rounded-xl font-bold text-xl flex items-center justify-center gap-2 bg-primary text-primary-foreground disabled:opacity-40 active:scale-95 transition-transform">
              {buscando ? "BUSCANDO…" : "AVANÇAR"} <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col select-none" data-e2e="tela-nome">
      <Cabecalho titulo="Seu nome" voltar={() => setEtapa("cpf")} />
      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-5 overflow-y-auto">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-black text-zinc-100 mb-1">Qual seu nome?</h2>
          <p className="text-base text-zinc-400">CPF {formatarCpf(digitos)} ainda não tem cadastro. Só o nome (pelo menos 3 letras) e pronto.</p>
        </div>
        {/* input de verdade (teclado físico funciona), mas sem abrir o teclado do sistema por cima do nosso */}
        <div className="relative w-full max-w-2xl">
          <input
            value={nome}
            onChange={(e) => { setErro(""); setNome(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter") cadastrar(); }}
            placeholder="Digite seu nome"
            inputMode="none"
            autoFocus
            data-e2e="nome-input"
            style={{ fontSize: "2.25rem" }}
            className="w-full h-20 pl-6 pr-20 text-center font-bold rounded-2xl bg-zinc-900 border-2 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-primary focus:outline-none"
          />
          <BotaoApagar
            onApagar={() => { setErro(""); setNome((n) => n.slice(0, -1)); }}
            onLimpar={() => { setErro(""); setNome(""); }}
            desabilitado={!nome}
          />
        </div>
        {erro && <p className="text-red-400 font-semibold">{erro}</p>}
        <TecladoAlfabetico
          onLetra={(l) => { setErro(""); acrescentar(l); }}
          onEspaco={() => setNome((n) => (n && !n.endsWith(" ") ? n + " " : n))}
        />
      </div>
      <div className="bg-zinc-900 border-t border-zinc-800 px-6 py-3">
        <div className="max-w-lg mx-auto">
          <button type="button" onClick={cadastrar} disabled={!nomeValido || buscando} data-e2e="nome-avancar" className="w-full h-14 rounded-xl font-bold text-xl flex items-center justify-center gap-2 bg-primary text-primary-foreground disabled:opacity-40 active:scale-95 transition-transform">
            AVANÇAR <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
